import {useEffect, useMemo, useRef} from 'react'
import Plyr, {PlyrInstance, PlyrProps} from 'plyr-react'
import {GlobalStyles} from './style'
import 'plyr-react/plyr.css'
import Hls from 'hls.js'
import {useBestResolutionUrl} from '../media-light-box/useBestResolutionUrl'
import {Video} from '@/features/host/types'
import cx from 'classnames'

interface CustomPlyrInstance extends Plyr {
    media: HTMLVideoElement
}

type CustomAPITypes = {
    plyr: CustomPlyrInstance
}

type VideoPlayerProps = {
    video: Video
    options?: Plyr.Options | null
}

const defaultVideoOptions = {
    frameBorder: '0',
    autoplay: true,
    allowFullScreen: true,
    tooltip: {controls: true, seek: true},
    clickToPlay: true,
    hideControls: false,
    controls: ['play-large', 'play', 'progress', 'current-time', 'mute', 'volume', 'fullscreen']
}

export const VideoPlayer = ({video, options = null, ...rest}: VideoPlayerProps) => {
    const {url: source} = useBestResolutionUrl({optimizedUrl: video.optimizedUrl, base: video.url})
    const isHLS = source?.includes('hls-master-playlist')
    const videoOptions = useMemo(
        () => (options ? {...defaultVideoOptions, ...options} : defaultVideoOptions),
        [options]
    )

    const ref = useRef<CustomAPITypes>(null)
    useEffect(() => {
        if (!source) return
        const loadVideo = () => {
            const video = document.getElementById('plyr') as HTMLVideoElement
            const hls = new Hls()
            hls.loadSource(source)
            hls.attachMedia(video)
            ref.current!.plyr.media = video

            hls.on(Hls.Events.MANIFEST_PARSED, function () {
                const {play} = ref.current!.plyr as PlyrInstance
                play()
            })
        }
        if (isHLS && source) {
            loadVideo()
        }
    }, [source])

    return (
        <>
            <GlobalStyles />
            <Plyr
                id="plyr"
                source={
                    isHLS
                        ? ({} as PlyrProps['source'])
                        : {
                              type: 'video',
                              sources: [
                                  {
                                      src: source || ''
                                  }
                              ]
                          }
                }
                ref={ref}
                options={videoOptions}
                {...rest}
                className={cx('plyr-react plyr')}
            />
        </>
    )
}

export default VideoPlayer
